/**
 * EditRepeatsModal — the "Edit repeats" action on a session page (11E.7).
 *
 * Scope semantics, and why "this" is not offered here
 * ---------------------------------------------------
 * Classcard's menu reads "this / this and following / all". In our model
 * "this" is not a series operation at all — it is `PATCH /sessions/{id}`,
 * already reachable by dragging the session on the calendar or via Edit.
 * Offering it here would give two different code paths for one action and
 * invite the operator to reshape a series when they meant to move one
 * lesson. So this modal offers only the two genuinely series-scoped edits:
 *
 *   following → split the class at this session; edits apply from here on
 *   all       → reshape the whole class; past sessions stay untouched
 *
 * ADR 0005
 * --------
 * The recurrence entered here is sent as wall-clock `HH:MM` plus an IANA
 * timezone and is expanded server-side in that timezone. Nothing in this
 * component converts times to UTC — doing so client-side is exactly the
 * DST bug ADR 0005 exists to prevent.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { Button, Modal } from '@/components/common';
import { ApiError } from '@core/api/client';
import { useEditRepeats, type SeriesEditScope } from '@features/calendar/api/sessions';
import type { ClassResponse } from '@features/calendar/api/classes';

export interface EditRepeatsModalProps {
  isOpen: boolean;
  sessionId: string;
  currentClass: ClassResponse;
  onClose: () => void;
}

const ISO_WEEKDAYS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

export const EditRepeatsModal: React.FC<EditRepeatsModalProps> = ({
  isOpen,
  sessionId,
  currentClass,
  onClose,
}) => {
  const recurrence = currentClass.recurrence;
  const [scope, setScope] = useState<SeriesEditScope>('following');
  const [days, setDays] = useState<number[]>(recurrence?.days_of_week ?? []);
  const [startTime, setStartTime] = useState(recurrence?.start_time ?? '');
  const [endTime, setEndTime] = useState(recurrence?.end_time ?? '');
  const [capacity, setCapacity] = useState(String(currentClass.capacity ?? ''));

  const mut = useEditRepeats();

  const toggleDay = (d: number) =>
    setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort()));

  const submit = async () => {
    if (!recurrence) {
      toast.error('This class has no recurrence to edit.');
      return;
    }
    if (recurrence.frequency === 'weekly' && days.length === 0) {
      toast.error('Pick at least one day of the week.');
      return;
    }
    try {
      const result = await mut.mutateAsync({
        id: sessionId,
        body: {
          scope,
          recurrence: {
            ...recurrence,
            days_of_week: recurrence.frequency === 'weekly' ? days : null,
            start_time: startTime,
            end_time: endTime,
          },
          capacity: capacity ? Number(capacity) : undefined,
        },
      });
      const preserved = result.sessions_preserved
        ? ` ${result.sessions_preserved} kept (attendance marked).`
        : '';
      toast.success(
        `${result.sessions_created} session(s) rescheduled.${preserved}` +
          (result.split ? ' Series split from this date.' : ''),
      );
      onClose();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not edit repeats');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit repeats"
      data-testid="candeza-session-edit-repeats-modal"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Apply to
          </legend>
          <label style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>
            <input
              type="radio"
              name="scope"
              value="following"
              data-testid="candeza-session-repeats-scope-following"
              checked={scope === 'following'}
              onChange={() => setScope('following')}
            />{' '}
            This and following sessions
          </label>
          <label style={{ display: 'block', fontSize: 14 }}>
            <input
              type="radio"
              name="scope"
              value="all"
              data-testid="candeza-session-repeats-scope-all"
              checked={scope === 'all'}
              onChange={() => setScope('all')}
            />{' '}
            All sessions in this class
          </label>
          <p style={{ fontSize: 12, color: '#64748b', margin: '6px 0 0' }}>
            Sessions that already have attendance marked are never changed or
            deleted — they stay exactly as recorded.
          </p>
        </fieldset>

        {recurrence?.frequency === 'weekly' && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Repeats on</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {ISO_WEEKDAYS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  data-testid={`candeza-session-repeats-day-${d.value}`}
                  aria-pressed={days.includes(d.value)}
                  onClick={() => toggleDay(d.value)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 6,
                    fontSize: 13,
                    cursor: 'pointer',
                    border: `1px solid ${days.includes(d.value) ? '#6366f1' : '#cbd5e1'}`,
                    background: days.includes(d.value) ? '#e0e7ff' : 'white',
                    color: days.includes(d.value) ? '#3730a3' : '#475569',
                  }}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>
            Start
            <input
              type="time"
              data-testid="candeza-session-repeats-start"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              style={{ display: 'block', marginTop: 4, padding: 6 }}
            />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600 }}>
            End
            <input
              type="time"
              data-testid="candeza-session-repeats-end"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              style={{ display: 'block', marginTop: 4, padding: 6 }}
            />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600 }}>
            Capacity
            <input
              type="number"
              min={1}
              data-testid="candeza-session-repeats-capacity"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              style={{ display: 'block', marginTop: 4, padding: 6, width: 90 }}
            />
          </label>
        </div>

        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
          Times are wall-clock in <strong>{recurrence?.timezone}</strong> and stay on
          that clock across daylight-saving changes.
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            data-testid="candeza-session-repeats-submit"
            onClick={() => void submit()}
            disabled={mut.isPending}
          >
            {mut.isPending ? 'Saving…' : 'Save repeats'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
